/**
 * Vertical Editions — frontend consumption of the per-tenant vertical config
 * served on the public branding payload (BrandingContext.vertical).
 *
 * Config shape (backend utils/vertical_templates.py is the source of truth):
 *   {
 *     industry:      'asset_financing',
 *     hidden_paths:  ['/admin/cases', ...],     // nav paths this Edition hides
 *     labels:        { 'Cases': 'Deals', ... }, // default-label → renamed label
 *     program_types: ['Financing'],             // product-form type options
 *     registration:  { skip_agreement_step, hide_portal_toggle, id_fields },
 *   }
 *
 * `vertical` is null for immigration tenants — every helper here treats null
 * as "no vertical behavior": labels resolve to their defaults, nothing is
 * hidden, and the registration flow is untouched. That null-path is the
 * immigration-regression guarantee.
 */

import { useBranding } from '../context/BrandingContext';

/** Resolve a display label through the vertical's rename map. */
export function resolveLabel(vertical, defaultLabel) {
  return (vertical && vertical.labels && vertical.labels[defaultLabel]) || defaultLabel;
}

/** Hook: the tenant's vertical config (null on immigration tenants). */
export function useVertical() {
  const branding = useBranding();
  return branding?.vertical || null;
}

/** Hook: label resolver bound to the current tenant. `l('Cases')` → 'Deals'. */
export function useLabels() {
  const vertical = useVertical();
  return (defaultLabel) => resolveLabel(vertical, defaultLabel);
}

/** Hook: is a lead-form field hidden for this Edition? Backed by the
 *  template's `hidden_fields` (e.g. EdTech hides nationality +
 *  country_of_interest — students enroll locally). A hidden field must also
 *  never be REQUIRED by a stage-gate modal. Null vertical hides nothing. */
export function useHiddenFields() {
  const vertical = useVertical();
  const hidden = (vertical && vertical.hidden_fields) || [];
  return (field) => hidden.includes(field);
}

/** True when `path` falls under one of the vertical's hidden nav paths.
 *  Matches exact path or a sub-path ('/admin/cases' hides '/admin/cases/123'
 *  but never '/admin/cases-report'). Query strings are ignored. */
export function isPathHidden(vertical, path) {
  if (!vertical || !Array.isArray(vertical.hidden_paths) || !path) return false;
  const clean = path.split('?')[0];
  return vertical.hidden_paths.some(
    (h) => clean === h || clean.startsWith(`${h}/`)
  );
}

/**
 * Apply the vertical config to a Sidebar menu-item array:
 *   1. drop submenu entries on hidden paths,
 *   2. drop top-level items whose own path is hidden AND have no surviving
 *      submenu; a parent with surviving children is kept and re-pointed at
 *      its first visible child so the group link never lands on a hidden page,
 *   3. rename labels through the vertical's map.
 * Returns the input array untouched (same reference) when vertical is null,
 * so immigration tenants render byte-identical menus.
 */
export function applyVerticalToMenu(items, vertical) {
  if (!vertical) return items;
  const out = [];
  for (const item of items) {
    let next = item;
    if (item.submenu) {
      const submenu = item.submenu
        .filter((s) => !isPathHidden(vertical, s.path))
        .map((s) => ({ ...s, label: resolveLabel(vertical, s.label) }));
      if (submenu.length === 0 && isPathHidden(vertical, item.path)) continue;
      next = { ...item, submenu };
      if (isPathHidden(vertical, item.path) && submenu.length > 0) {
        next.path = submenu[0].path;
      }
    } else if (isPathHidden(vertical, item.path)) {
      continue;
    }
    out.push({ ...next, label: resolveLabel(vertical, next.label) });
  }
  return out;
}
